import { useEffect, useRef, useState } from 'react';

type Options = {
  delayMs: number;
  minVisibleMs: number;
};

export default function useDelayedBusyState(
  isBusy: boolean,
  { delayMs, minVisibleMs }: Options,
) {
  const [isVisible, setIsVisible] = useState(false);
  const showTimeoutRef = useRef<number | null>(null);
  const hideTimeoutRef = useRef<number | null>(null);
  const visibleSinceRef = useRef<number | null>(null);

  useEffect(() => {
    if (isBusy) {
      if (hideTimeoutRef.current !== null) {
        window.clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }

      if (isVisible || showTimeoutRef.current !== null) {
        return;
      }

      showTimeoutRef.current = window.setTimeout(() => {
        showTimeoutRef.current = null;
        visibleSinceRef.current = Date.now();
        setIsVisible(true);
      }, delayMs);

      return;
    }

    if (showTimeoutRef.current !== null) {
      window.clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }

    if (!isVisible) {
      return;
    }

    const elapsed = visibleSinceRef.current
      ? Date.now() - visibleSinceRef.current
      : minVisibleMs;
    const remaining = Math.max(minVisibleMs - elapsed, 0);

    hideTimeoutRef.current = window.setTimeout(() => {
      hideTimeoutRef.current = null;
      visibleSinceRef.current = null;
      setIsVisible(false);
    }, remaining);
  }, [delayMs, isBusy, isVisible, minVisibleMs]);

  useEffect(() => {
    return () => {
      if (showTimeoutRef.current !== null) {
        window.clearTimeout(showTimeoutRef.current);
      }
      if (hideTimeoutRef.current !== null) {
        window.clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  return isVisible;
}
